import { useChatFileUploadEnabled } from "@/components/feature/Chat/hooks/useChatFileUploadEnabled"
import { isPersistedConversationId } from "@/components/feature/Chat/utils/chatUtils"
import { useChatContext } from "@/contexts/ChatContext"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import {
  highlightTarget,
  planAction,
} from "@/features/agent-chat/actions/actionExecutor"
import {
  AgentTarget,
  resolveTargetKeysForRoute,
} from "@/features/agent-chat/actions/agentTargets"
import { pulseTarget } from "@/features/agent-chat/actions/pulse"
import {
  executeToolCall as runToolCall,
  ToolExecutorDeps,
} from "@/features/agent-chat/actions/toolExecutor"
import { waitForSelector } from "@/features/agent-chat/actions/waitForSelector"
import {
  AgentPageContextValue,
  useAgentPageContext,
} from "@/features/agent-chat/context/AgentPageContext"
import {
  AgentAction,
  ProposalCard,
  ToolCallResult,
} from "@/features/agent-chat/types"
import { NOTE_WRITE_PERMISSION } from "@/features/material-compliance/api/notesApi"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  chatFilesApiCalls,
  ConversationFile,
} from "@/services/api/chatFilesService"
import { questionnaireApiCalls } from "@/services/api/questionnaireService"
import { ApiCitation } from "@/types/questionnaire/api"
import { useQueryClient } from "@tanstack/react-query"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useLocation, useNavigate } from "react-router"

/**
 * Any `*_id` (or bare `id`) field seen in a tool result or a page's
 * registered context is remembered for the rest of the chat session and
 * merged into every subsequent request's page context. This is what lets
 * "fill this questionnaire" (uploaded from chat, no page registering it)
 * stay resolvable in later turns without a dedicated per-feature mechanism —
 * chat history only persists the final text of a turn, not tool traffic.
 */
const ENTITY_ID_PATTERN = /(^id|_id)$/i

/** Max files attachable per conversation — staged + still-uploading combined. */
const MAX_CHAT_ATTACHMENTS = 8

function rememberEntityIds(
  target: Record<string, unknown>,
  source: Record<string, unknown> | null | undefined,
): void {
  if (!source) return
  for (const [key, value] of Object.entries(source)) {
    if (
      ENTITY_ID_PATTERN.test(key) &&
      (typeof value === "string" || typeof value === "number")
    ) {
      target[key] = value
    }
  }
}

/** The backend's `detail.message` for a failed request, when there is one —
 * same extraction `toolExecutor.ts`'s `apiMessage` does, kept local here since
 * this is the only other place in the agent-chat feature that needs it. */
function apiErrorMessage(error: unknown): string | undefined {
  const detail = (
    error as {
      response?: { data?: { detail?: { message?: string } | string } }
    }
  )?.response?.data?.detail
  if (typeof detail === "string") return detail
  return detail?.message
}

export interface PendingAction {
  id: number
  /** Route to navigate to on confirm. */
  route: string
  /** Target to pulse after navigation, if any. */
  target: AgentTarget | null
  /** Human-readable label for the affordance. */
  label: string
  /** See ProposalCard.anchorMessageCount. */
  anchorMessageCount: number
  /**
   * "confirmed" once the user has clicked through — kept in `pendingActions`
   * (not dismissed) so the card stays visible as a "you went there" marker
   * instead of vanishing from the chat history.
   */
  status: "pending" | "confirmed"
}

/** A blocking tool call awaiting the user's Confirm / Cancel decision. */
export interface PendingConfirmation {
  title: string
  detail: string
  /**
   * "pending" — showing Confirm/Cancel. "confirmed" — the user clicked
   * Confirm and the card stays mounted (buttons replaced by a working
   * indicator) until the tool call this unblocked actually finishes; cleared
   * by `executeToolCall`. Without this the card vanished the instant Confirm
   * was clicked, leaving the whole network round-trip with no feedback.
   */
  status: "pending" | "confirmed"
}

/** A single answer explicitly targeted via the per-answer "ask agent" button. */
export interface TargetedAnswer {
  questionnaireId: string
  responsePairId: string
  questionLabel: string
}

/**
 * A help article explicitly targeted via the "ask the agent about this" button on an
 * article page. Mirrors TargetedAnswer rather than prefilling the composer: the user
 * still writes their own question, and the article is context the page registers
 * alongside it, so the agent knows what "this" refers to without the text being faked.
 */
export interface TargetedHelpArticle {
  articleId: string
  title: string
}

export interface AgentChatContextValue {
  pendingActions: PendingAction[]
  confirmAction: (action: PendingAction) => Promise<void>
  dismissAction: (id: number) => void
  proposals: ProposalCard[]
  acceptProposal: (id: string) => Promise<void>
  rejectProposal: (id: string) => void
  /**
   * Files attached in the current conversation — staged server-side the
   * moment they are attached (see `chatFilesService`). Replaces the old
   * single in-browser `heldFile`: several files can be attached at once, each
   * independently addressable and removable, and they survive a refresh
   * because the bytes never lived only in this tab.
   */
  stagedFiles: ConversationFile[]
  /**
   * Files currently being staged server-side (POST in flight) — a file only
   * joins `stagedFiles` once that request resolves, so the composer uses
   * this list to render a loading chip in the meantime.
   */
  uploadingFiles: UploadingFile[]
  /**
   * Attach a file: stages it immediately. If the conversation has no
   * persisted id yet (a brand-new chat), one is created first — staging is
   * conversation-scoped storage, so a file cannot be attached to a
   * conversation that does not exist yet. Requires CHAT_HISTORY; the
   * composer only offers attaching when both flags are on.
   */
  attachFile: (file: File) => Promise<void>
  /** Explicit removal from the composer — hard-deletes the staged object. */
  removeStagedFile: (fileId: string) => Promise<void>
  pendingConfirmation: PendingConfirmation | null
  resolveConfirmation: (confirmed: boolean) => void
  /** What the user is currently viewing, merged with remembered entity ids. */
  effectivePageContext: AgentPageContextValue
  /** Answer explicitly targeted via a per-answer "ask agent" button, if any. */
  targetedAnswer: TargetedAnswer | null
  setTargetedAnswer: (target: TargetedAnswer) => void
  clearTargetedAnswer: () => void
  targetedHelpArticle: TargetedHelpArticle | null
  setTargetedHelpArticle: (target: TargetedHelpArticle) => void
  clearTargetedHelpArticle: () => void
  /**
   * Dispatch a streamed agent action (navigate/highlight/proposal).
   * `anchorMessageCount` positions any resulting card in the message thread
   * (see ProposalCard.anchorMessageCount); omit it for call sites with no
   * message-thread position of their own — it defaults to "render at the end".
   */
  handleAction: (action: AgentAction, anchorMessageCount?: number) => void
  /** Run a blocking tool call the frontend must execute. */
  /**
   * Dispatch any navigation offers a write queued during this turn, anchored to
   * the given message count so each card stays with the reply that earned it.
   */
  flushQueuedNavigations: (anchorMessageCount: number) => void
  executeToolCall: (
    toolCall: Extract<AgentAction, { type: "tool_call" }>,
  ) => Promise<ToolCallResult>
  /**
   * Whichever chat surface is mounted (docked/floating/full-page) registers
   * its own "append a standalone assistant message" callback here — actions,
   * proposals and tool-call handlers use it for fallback/failure notices.
   */
  registerAppendAssistantMessage: (fn: (text: string) => void) => void
  /**
   * A page displaying questionnaire answers registers here how to apply an
   * accepted answer to what it renders. Called after the answer is saved
   * server-side, with everything needed to update the view in place.
   *
   * Refetching (or invalidating the react-query cache) is NOT enough on the
   * answers page: it snapshots the fetched questionnaire into local state so
   * edits stay instant, so fresh server data never reaches the rendered copy.
   * The page has to fold the change into that local copy itself.
   */
  registerAnswerAcceptedListener: (
    fn: (accepted: AcceptedAnswer) => void,
  ) => void
  /**
   * Call this from wherever a conversation genuinely starts fresh or switches
   * to a different one (New Chat, selecting a different conversation from
   * history, a URL-driven switch) — clears proposals/pending actions/
   * remembered entity ids so a stale card or id from the previous
   * conversation doesn't carry into the new one.
   */
  resetForNewConversation: () => void
}

/** A file whose staging POST hasn't resolved yet — see `uploadingFiles`. */
export interface UploadingFile {
  id: string
  fileName: string
}

/** An answer the user applied from a proposal card, already saved server-side. */
export interface AcceptedAnswer {
  questionnaireId: string
  responsePairId: string
  response: string
  citations: ApiCitation[]
}

const AgentChatContext = createContext<AgentChatContextValue | undefined>(
  undefined,
)

export const AgentChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const location = useLocation()
  const pathRef = useRef(location.pathname)
  pathRef.current = location.pathname
  // What the current page registered about itself. A ref, not the state value,
  // because `handleAction` is a stable callback and would otherwise close over
  // whatever was registered when it was created. Read by `planAction` for
  // stage-scoped controls, where the pathname cannot say whether the user is
  // already looking at the thing being pointed at.
  const registeredEntitiesRef = useRef<Record<string, unknown> | undefined>(
    undefined,
  )
  // Read via ref: handleAction is memoized and must not re-create (and drop
  // queued actions) every time the flag map identity changes.
  const featureFlags = useGetAllFeatureFlags()
  const chatFileUploadEnabled = useChatFileUploadEnabled()
  const featureFlagsRef = useRef(featureFlags)
  featureFlagsRef.current = featureFlags
  const { translate, currentLanguage } = useTranslation()
  const canWriteInquiryNotes = usePermissionGuard(
    NOTE_WRITE_PERMISSION.inquiry,
  ).canRun
  const queryClient = useQueryClient()
  // AgentChatProvider is mounted inside ChatProvider (see App.tsx), so it can
  // read/advance the active conversation directly — needed to mint a
  // conversation id on first attach, before any message has been sent.
  const { chat, setChat } = useChatContext()

  const idRef = useRef(0)
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  const [proposals, setProposals] = useState<ProposalCard[]>([])
  const [stagedFiles, setStagedFiles] = useState<ConversationFile[]>([])
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  // Continuation loop spanning several requests needs the LIVE conversation id
  // read at call time, same reasoning as the old heldFileRef: a state snapshot
  // from the render that produced this context value can lose a race against
  // the next request going out in the same tick.
  const conversationIdRef = useRef<string | null>(
    isPersistedConversationId(chat.id) ? chat.id : null,
  )
  conversationIdRef.current = isPersistedConversationId(chat.id)
    ? chat.id
    : null
  const stagedFilesRef = useRef<ConversationFile[]>([])
  stagedFilesRef.current = stagedFiles
  // Two counters, tracked directly rather than mirrored from render (several
  // attachFile calls can fire back-to-back faster than React commits a
  // render between them, so a render-derived ref lags a commit behind and
  // undercounts). Kept SEPARATE rather than merged into one "total" number:
  // `confirmedCountRef` always mirrors `stagedFiles.length` (updated at the
  // same call sites, in lockstep with every setStagedFiles call — including
  // the listFiles GET, which reconciles by id instead of replacing wholesale
  // for exactly this reason; see that effect), while `pendingCountRef` is
  // purely the count of attachFile calls reserved but not yet settled. An
  // earlier version merged the GET's count in via `Math.max(count,
  // files.length)`, which silently replaced an in-flight reservation with
  // the (unrelated) loaded count instead of adding to it — e.g. a
  // conversation with 7 files already staged, plus one attach in flight when
  // the GET resolves, collapsed 7+1=8 down to 7 and let an 8th attach
  // through, landing 9. Summing the two counters (never merging one into the
  // other) can't do that: a reservation always adds on top of whatever the
  // last GET/mutation confirmed.
  const confirmedCountRef = useRef(0)
  const pendingCountRef = useRef(0)
  // Ids removeStagedFile has already deleted server-side, for the current
  // conversation. A listFiles GET fired before that DELETE (e.g. right after
  // ensureConversationId mints this conversation) can resolve AFTER it and
  // still list the now-gone file — without this, the reconciliation below
  // would treat that stale entry as "known locally but missing from prev, so
  // re-add it," resurrecting the chip and its slot in the cap permanently
  // (this effect only runs once per conversation id, so nothing would ever
  // correct it). Cleared whenever the conversation changes, since ids don't
  // carry across conversations.
  const removedFileIdsRef = useRef<Set<string>>(new Set())
  const [targetedAnswer, setTargetedAnswerState] =
    useState<TargetedAnswer | null>(null)
  const [targetedHelpArticle, setTargetedHelpArticleState] =
    useState<TargetedHelpArticle | null>(null)
  /** Conversation-scoped memory of entity ids seen so far (see rememberEntityIds). */
  const entityMemoryRef = useRef<Record<string, unknown>>({})
  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation | null>(null)
  const confirmationResolverRef = useRef<((confirmed: boolean) => void) | null>(
    null,
  )
  /** Latest "append assistant message" callback, wired by whichever chat surface is mounted. */
  const appendAssistantMessageRef = useRef<((text: string) => void) | null>(
    null,
  )
  /** Latest "an answer was accepted" callback, wired by the page showing it. */
  const answerAcceptedListenerRef = useRef<
    ((accepted: AcceptedAnswer) => void) | null
  >(null)

  // Deliberately NOT inferred from `chat.id`/`chat.created_at` changing —
  // that was tried and is unreliable: the ongoing conversation's `id` moves
  // from the unpersisted placeholder to its own freshly-assigned real id
  // mid-turn (see onConversationId in useChatSubmit.ts), and the
  // message-hydration query that activates right after ALSO recomputes
  // `created_at` from the server's timestamp — but as two separate renders,
  // not one, so no single-effect guard over both values can tell that apart
  // from an actual conversation switch. Callers that genuinely start a new
  // conversation or switch to a different one call this directly instead
  // (see useChatActions.ts, ChatThreadList.tsx, ChatPanel.tsx,
  // useConversationRouting.ts's URL-driven switch).
  const resetForNewConversation = useCallback(() => {
    setPendingActions([])
    setProposals([])
    setStagedFiles([])
    setUploadingFiles([])
    confirmedCountRef.current = 0
    pendingCountRef.current = 0
    removedFileIdsRef.current = new Set()
    // A pending confirmation belongs to a tool call in the conversation being
    // left behind. Settling it `false` is what actually unblocks that
    // suspended promise — dropping the card alone would leave it hanging
    // forever, and (now that the card locks the composer) the new
    // conversation's input locked with it.
    confirmationResolverRef.current?.(false)
    confirmationResolverRef.current = null
    setPendingConfirmation(null)
    entityMemoryRef.current = {}
  }, [])

  // Load this conversation's staged files whenever the persisted conversation
  // id changes (switching conversations, or the id moving from the
  // unpersisted placeholder to a real one after the first turn). Attaching a
  // file itself updates `stagedFiles` directly (see attachFile) rather than
  // waiting on this effect, so the composer reflects it immediately.
  // Skipped entirely while file upload is off: every files route 403s then.
  useEffect(() => {
    const conversationId = isPersistedConversationId(chat.id) ? chat.id : null
    if (!conversationId || !chatFileUploadEnabled) {
      setStagedFiles([])
      confirmedCountRef.current = 0
      removedFileIdsRef.current = new Set()
      return
    }
    let cancelled = false
    chatFilesApiCalls
      .listFiles(conversationId)
      .then((files) => {
        if (!cancelled) {
          // Reconciled by id against whatever's already in `stagedFiles`,
          // NOT a plain replace: this GET can resolve after an attach that
          // started later already landed (a conversation id just minted by
          // ensureConversationId has nothing server-side yet when the GET
          // fires, so it commonly loses this race against the follow-up
          // POST; a slow GET on an existing conversation with a fresh attach
          // racing it hits the same thing). A file present locally but
          // absent from this response is one this snapshot predates, not one
          // that stopped existing — dropping it would both make its chip
          // vanish and silently free up a slot in the cap it should still
          // occupy. The same stale-snapshot risk cuts the other way for a
          // file that WAS removed meanwhile: `files` can still list it (this
          // GET was in flight before that DELETE landed), so it's excluded
          // via removedFileIdsRef rather than trusted just because it
          // appears in the response — otherwise a removed chip (and its cap
          // slot) would come back permanently, since this effect never
          // reruns for the same conversation id.
          setStagedFiles((prev) => {
            const removed = removedFileIdsRef.current
            const prevIds = new Set(prev.map((f) => f.id))
            const fromGet = files.filter(
              (f) => !removed.has(f.id) && !prevIds.has(f.id),
            )
            const merged = [...prev, ...fromGet]
            confirmedCountRef.current = merged.length
            return merged
          })
        }
      })
      .catch(() => {
        // A failed list is not fatal — the composer simply shows no chips
        // until the next successful load (e.g. after attaching another file).
      })
    return () => {
      cancelled = true
    }
  }, [chat.id, chatFileUploadEnabled])

  const runHighlight = useCallback(
    async (target: AgentTarget) => {
      await highlightTarget(target, {
        waitForSelector: (selector, timeoutMs) =>
          timeoutMs === undefined
            ? waitForSelector(selector)
            : waitForSelector(selector, timeoutMs),
        pulse: pulseTarget,
        postAssistantMessage: (text) =>
          appendAssistantMessageRef.current?.(text),
        translate,
      })
    },
    [translate],
  )

  const handleAction = useCallback(
    (action: AgentAction, anchorMessageCount: number = Infinity) => {
      // Bucket 3: proposal cards
      if (action.type === "proposal") {
        setProposals((prev) => [
          ...prev,
          {
            id: `${action.responsePairId}-${Date.now()}`,
            responsePairId: action.responsePairId,
            questionnaireId: action.questionnaireId,
            question: action.question,
            draftAnswer: action.draftAnswer,
            citations: action.citations,
            status: "pending" as const,
            anchorMessageCount,
          },
        ])
        return
      }

      // Blocking tool calls are handled by the submit loop directly.
      if (action.type === "tool_call") {
        return
      }

      // Bucket 1: navigation / highlight
      const plan = planAction(
        action,
        pathRef.current,
        featureFlagsRef.current,
        registeredEntitiesRef.current,
      )
      switch (plan.kind) {
        case "pulse":
          void runHighlight(plan.target)
          break
        case "navigate":
          setPendingActions((prev) => [
            ...prev,
            {
              id: ++idRef.current,
              route: plan.route,
              target: plan.target,
              label:
                (plan.target?.labelKey
                  ? translate(plan.target.labelKey)
                  : plan.target?.label) ??
                (plan.labelKey ? translate(plan.labelKey) : plan.label) ??
                translate("AI_CHAT/OPEN_PAGE_DEFAULT_LABEL"),
              anchorMessageCount,
              status: "pending" as const,
            },
          ])
          break
        case "unknown-target":
          appendAssistantMessageRef.current?.(
            translate("AI_CHAT/TARGET_NOT_FOUND"),
          )
          break
        case "noop":
          break
      }
    },
    [runHighlight, translate],
  )

  const requestConfirmation = useCallback(
    (title: string, detail: string): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        // Only one pending confirmation at a time; cancel a stale one.
        confirmationResolverRef.current?.(false)
        confirmationResolverRef.current = resolve
        setPendingConfirmation({ title, detail, status: "pending" })
      })
    },
    [],
  )

  const resolveConfirmation = useCallback((confirmed: boolean) => {
    confirmationResolverRef.current?.(confirmed)
    confirmationResolverRef.current = null
    if (confirmed) {
      // Keep the card mounted — the tool call it unblocks (e.g. route_file's
      // network round-trip) can take seconds, and nulling here left that
      // whole gap with nothing on screen. executeToolCall clears it once the
      // tool call actually finishes, success or failure.
      setPendingConfirmation((prev) =>
        prev ? { ...prev, status: "confirmed" } : prev,
      )
    } else {
      setPendingConfirmation(null)
    }
  }, [])

  const ensureConversationId = useCallback((): string => {
    if (conversationIdRef.current) return conversationIdRef.current
    // Staging is conversation-scoped storage, so a file needs SOME id to
    // stage against — but minting a real row via POST /chat/conversations
    // here was wrong: it burned a slot against MAX_CONVERSATIONS_PER_USER for
    // every "attach then abandon" chat, since a file attached and never sent
    // counted as a real conversation forever (confirmed in testing: hit the
    // 50-conversation cap purely from repeated attach testing).
    //
    // `conversation_files.conversation_id` is deliberately NOT a foreign key
    // (see the schema's docstring) for exactly this reason: a client-minted
    // id needs no row to exist yet. useChatSubmit.ts already mints one this
    // same way for a fresh chat's first message ("FE mints it from turn 1,"
    // honored by persist_chat_turn, which creates the row lazily under
    // whatever id it's given) — reusing that pattern here, instead of a
    // second REST-backed path, is what keeps the two in sync: setting
    // `chat.id` now means useChatSubmit's own mint check
    // (`!persistedConversationId && !clientConversationIdRef.current`) sees
    // an already-persisted-looking id and reuses THIS one for the first
    // message, so the file and the turn land under the same conversation.
    const id = crypto.randomUUID()
    conversationIdRef.current = id
    setChat((prev) => ({ ...prev, id, updated_at: new Date().toISOString() }))
    return id
  }, [setChat])

  const attachFile = useCallback(
    async (file: File) => {
      if (
        confirmedCountRef.current + pendingCountRef.current >=
        MAX_CHAT_ATTACHMENTS
      ) {
        appendAssistantMessageRef.current?.(
          translate("AI_CHAT/ATTACH_FILE_LIMIT_REACHED", {
            max: MAX_CHAT_ATTACHMENTS,
          }),
        )
        return
      }
      // Reserved synchronously (not derived from stagedFiles/uploadingFiles
      // state) so a second attach fired before this one's state lands still
      // sees the reservation and can't slip past the cap.
      pendingCountRef.current += 1
      const uploadingId = `uploading-${idRef.current++}`
      setUploadingFiles((prev) => [
        ...prev,
        { id: uploadingId, fileName: file.name },
      ])
      try {
        const conversationId = await ensureConversationId()
        const staged = await chatFilesApiCalls.stageFile(conversationId, file)
        pendingCountRef.current -= 1
        confirmedCountRef.current += 1
        setStagedFiles((prev) => [...prev, staged])
      } catch (error) {
        pendingCountRef.current -= 1
        // Called fire-and-forget from ChatInput's onChange, so this is the only
        // place a 415 (wrong type) or 413 (too large) — both real, expected
        // paths in validate_chat_attachment — ever reaches the user.
        const reason = apiErrorMessage(error)
        appendAssistantMessageRef.current?.(
          reason
            ? translate("AI_CHAT/ATTACH_FILE_ERROR_DETAIL", {
                fileName: file.name,
                reason,
              })
            : translate("AI_CHAT/ATTACH_FILE_ERROR_GENERIC", {
                fileName: file.name,
              }),
        )
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingId))
      }
    },
    [ensureConversationId, translate],
  )

  const removeStagedFile = useCallback(
    async (fileId: string) => {
      const conversationId = conversationIdRef.current
      if (!conversationId) return
      // Called fire-and-forget from the chip's remove button — without this,
      // a failed removal (network error, already-gone file) left the chip
      // sitting there with no explanation for why nothing happened.
      const fileName =
        stagedFilesRef.current.find((f) => f.id === fileId)?.fileName ??
        "the file"
      try {
        await chatFilesApiCalls.removeFile(conversationId, fileId)
        setStagedFiles((prev) => prev.filter((f) => f.id !== fileId))
        confirmedCountRef.current = Math.max(0, confirmedCountRef.current - 1)
        removedFileIdsRef.current.add(fileId)
      } catch (error) {
        const reason = apiErrorMessage(error)
        appendAssistantMessageRef.current?.(
          reason
            ? translate("AI_CHAT/REMOVE_FILE_ERROR_DETAIL", {
                fileName,
                reason,
              })
            : translate("AI_CHAT/REMOVE_FILE_ERROR_GENERIC", { fileName }),
        )
      }
    },
    [translate],
  )

  const setTargetedAnswer = useCallback((target: TargetedAnswer) => {
    setTargetedAnswerState(target)
  }, [])

  const clearTargetedAnswer = useCallback(() => {
    setTargetedAnswerState(null)
  }, [])

  const setTargetedHelpArticle = useCallback((target: TargetedHelpArticle) => {
    setTargetedHelpArticleState(target)
  }, [])

  const clearTargetedHelpArticle = useCallback(() => {
    setTargetedHelpArticleState(null)
  }, [])

  /**
   * Navigation offers from write tools, held until the turn ends. The write runs
   * between the tool-call round and the round that narrates it, so dispatching one
   * as it happens puts the button on screen ahead of the text explaining it.
   */
  const queuedNavigationsRef = useRef<AgentAction[]>([])

  const flushQueuedNavigations = useCallback(
    (anchorMessageCount: number) => {
      const queued = queuedNavigationsRef.current
      queuedNavigationsRef.current = []
      // One card per destination: a create followed by an edit of the same draft
      // offers the identical route twice, and two buttons for one instruction
      // read as two separate outcomes.
      const seen = new Set<string>()
      queued.forEach((action) => {
        const key = action.type === "navigate" ? action.route : action.type
        if (seen.has(key)) return
        seen.add(key)
        // Anchored to THIS turn. Without a real count handleAction defaults to
        // Infinity, which ChatInterface re-clamps to the current message count on
        // every render — so the card stayed pinned below the newest reply and the
        // "Done:" markers piled up turn after turn.
        handleAction(action, anchorMessageCount)
      })
    },
    [handleAction],
  )

  const executeToolCall = useCallback(
    async (
      toolCall: Extract<AgentAction, { type: "tool_call" }>,
    ): Promise<ToolCallResult> => {
      const deps: ToolExecutorDeps = {
        conversationId: conversationIdRef.current,
        stagedFiles: stagedFilesRef.current,
        // The row survives routing (see the design doc's "Reading after
        // routing") — mark it filed rather than removing the chip, so the
        // composer shows what happened instead of the file silently vanishing.
        onFileRouted: (fileId, destination) => {
          setStagedFiles((prev) =>
            prev.map((f) =>
              f.id === fileId
                ? { ...f, status: "routed", routedTo: destination }
                : f,
            ),
          )
        },
        requestConfirmation,
        postAssistantMessage: (text) =>
          appendAssistantMessageRef.current?.(text),
        queueNavigation: (action) => {
          queuedNavigationsRef.current.push(action)
        },
        queryClient,
        // Inquiry email templates exist in EN/DE only: DE for a DE UI, else EN
        // (incl. JA). Same fallback useCreateInquiries applies.
        uiLanguage: currentLanguage === "DE" ? "de" : "en",
        canWriteInquiryNotes,
        translate,
      }
      const result = await runToolCall(toolCall, deps)
      // The confirmed-card's working indicator (see resolveConfirmation) is
      // scoped to "however long this tool call takes" — clear it here so it
      // disappears exactly when the thing it was waiting on actually finishes.
      setPendingConfirmation(null)
      rememberEntityIds(entityMemoryRef.current, result.content)
      return result
    },
    [
      requestConfirmation,
      queryClient,
      currentLanguage,
      canWriteInquiryNotes,
      translate,
    ],
  )

  const registeredContext = useAgentPageContext()
  registeredEntitiesRef.current = registeredContext?.entities

  // Entities a page registers (e.g. questionnaire_id on pdf-answers) stay
  // remembered even after the user navigates away, same as tool-result ids.
  useEffect(() => {
    rememberEntityIds(entityMemoryRef.current, registeredContext?.entities)
  }, [registeredContext])

  // Every page gets at least route-level awareness, even ones that never
  // call useRegisterAgentContext; registered/remembered entities layer on top.
  const effectiveRoute = registeredContext?.route ?? pathRef.current
  // The backend's highlight_ui outcome_resolver (see builtin_tools.py) reads
  // this to compute whether a target is already visible vs. needs a real
  // navigation — a fact, not something the model has to guess from a raw
  // pathname it has no route table for. The frontend owns AGENT_TARGETS, so
  // it's the only place that can resolve pathname -> target_key.
  const visibleTargetKeys = resolveTargetKeysForRoute(effectiveRoute)
  const effectivePageContext: AgentPageContextValue = {
    route: effectiveRoute,
    entities: {
      ...entityMemoryRef.current,
      ...(registeredContext?.entities ?? {}),
      ...(visibleTargetKeys.length > 0
        ? { visible_target_keys: visibleTargetKeys }
        : {}),
    },
  }

  const dismissAction = useCallback((id: number) => {
    setPendingActions((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const navigate = useNavigate()

  const confirmAction = useCallback(
    async (action: PendingAction) => {
      setPendingActions((prev) =>
        prev.map((a) =>
          a.id === action.id ? { ...a, status: "confirmed" as const } : a,
        ),
      )
      navigate(action.route)
      if (action.target) {
        await runHighlight(action.target)
      }
    },
    [navigate, runHighlight],
  )

  const acceptProposal = useCallback(
    async (id: string) => {
      const proposal = proposals.find((p) => p.id === id)
      if (!proposal || proposal.status !== "pending") return

      setProposals((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, status: "accepted" as const } : p,
        ),
      )

      // Show the answer straight away rather than after the save round-trip —
      // waiting on the network here is what made Apply feel laggy. This matches
      // how typing in an answer box already behaves on the answers page: the
      // view updates on the spot and the save happens behind it.
      answerAcceptedListenerRef.current?.({
        questionnaireId: proposal.questionnaireId,
        responsePairId: proposal.responsePairId,
        response: proposal.draftAnswer,
        citations: proposal.citations,
      })

      try {
        await questionnaireApiCalls.editResponsePair({
          questionnaireId: proposal.questionnaireId,
          responsePairId: proposal.responsePairId,
          response: proposal.draftAnswer,
          citations: proposal.citations,
        })
      } catch {
        // Put the card back so the user can retry. The answer box keeps showing
        // the unsaved text, same as a failed manual edit does.
        setProposals((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, status: "pending" as const } : p,
          ),
        )
        appendAssistantMessageRef.current?.(
          translate("AI_CHAT/PROPOSAL_SAVE_ERROR"),
        )
      }
    },
    [proposals, translate],
  )

  const rejectProposal = useCallback((id: string) => {
    setProposals((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, status: "rejected" as const } : p,
      ),
    )
  }, [])

  const registerAppendAssistantMessage = useCallback(
    (fn: (text: string) => void) => {
      appendAssistantMessageRef.current = fn
    },
    [],
  )

  const registerAnswerAcceptedListener = useCallback(
    (fn: (accepted: AcceptedAnswer) => void) => {
      answerAcceptedListenerRef.current = fn
    },
    [],
  )

  const value: AgentChatContextValue = {
    pendingActions,
    confirmAction,
    dismissAction,
    proposals,
    acceptProposal,
    rejectProposal,
    stagedFiles,
    uploadingFiles,
    attachFile,
    removeStagedFile,
    pendingConfirmation,
    resolveConfirmation,
    effectivePageContext,
    targetedAnswer,
    setTargetedAnswer,
    clearTargetedAnswer,
    targetedHelpArticle,
    setTargetedHelpArticle,
    clearTargetedHelpArticle,
    handleAction,
    flushQueuedNavigations,
    executeToolCall,
    registerAppendAssistantMessage,
    registerAnswerAcceptedListener,
    resetForNewConversation,
  }

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  )
}

export const useAgentChatContext = (): AgentChatContextValue => {
  const context = useContext(AgentChatContext)
  if (!context) {
    throw new Error("useAgentChatContext must be used within AgentChatProvider")
  }
  return context
}
